import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '../../firebase'; // Assuming auth is needed for current user check
import { useAuth } from '../contexts/AuthContext';
import { FiAward, FiClock, FiBarChart2, FiStar } from 'react-icons/fi';
import { MathJax, MathJaxContext } from 'better-react-mathjax'; // Import MathJax components
import { formatCustomTagsToLaTeX } from '../common/QuestionFormatter'; // Import formatting helper

// MathJax configuration (can reuse the same one)
const mathJaxConfig = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    inlineMath: [["$", "$"], ["\\(", "\\)"]], // $...$ and \(...\)
    displayMath: [["$$", "$$"], ["\\[", "\\]"]], // $$...$$ and \[...\]
    processEscapes: true,
    macros: { // Add support for \ce{} macro
      '\\ce': '{\\text{#1}}'
    }
  },
  options: { // Optional: Configure MathJax options if needed
    enableMenu: false, // Disable right-click MathJax menu
  },
};

function QuizBattleResults() {
  const { battleId } = useParams(); // Use battleId instead of quizId
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participant');
  const score = searchParams.get('score'); // Score might be passed via param or calculated
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [battleData, setBattleData] = useState(null); // Use battleData instead of quiz
  const [participant, setParticipant] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState(null);
  // Flagging state - keep for now, might be useful to review answers
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentFlaggedQuestion, setCurrentFlaggedQuestion] = useState(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [flagReason, setFlagReason] = useState('');

   const flagReasons = [
    'Incorrect answer',
    'Unclear question',
    'Grammatical error',
    'Technical issue',
    'Other'
  ];

   // Handle flagging - adapt to battle structure if needed
  const handleFlagQuestion = (questionId) => {
    // This needs to be adapted to the questions within the battleData
    console.log('Flagging not fully implemented for battle results yet.');
    // setCurrentFlaggedQuestion(questionId);
    // setShowFlagModal(true);
  };

  // Submit flag - adapt to battle structure if needed
   const submitFlag = async () => {
      // This needs to be adapted to the questions within the battleData
      console.log('Flag submission not fully implemented for battle results yet.');
      // if (!flagReason) return;
      // try { ...addDoc to flagged_questions...} catch (error) { ... }
   };

  useEffect(() => {
    let isMounted = true;

    const fetchResults = async () => {
      try {
        // Fetch battle data
        const battleRef = doc(db, 'quiz_battles', battleId);
        const battleDoc = await getDoc(battleRef);

        if (!battleDoc.exists()) {
          throw new Error('Quiz Battle not found');
        }
        const battleData = battleDoc.data();

        // Fetch participant data
        const participantRef = doc(db, 'public-quiz-participants', participantId);
        const participantDoc = await getDoc(participantRef);

        if (!participantDoc.exists()) {
          throw new Error('Participant results not found');
        }
        const participantData = participantDoc.data();

        if (isMounted) {
          setBattleData(battleData);
          setParticipant(participantData);
          setLoading(false);
          // Set initial score if not passed as param (e.g., if coming from direct link)
          if (score === null) {
             // Calculate score if needed, although it should be in participantData
             // const calculatedScore = calculateScore(battleData.questions, participantData.answers);
             // setScore(calculatedScore); // Assuming score state exists or is used from participant
          }
        }

        // Fetch leaderboard data
        try {
          console.log('Attempting to fetch leaderboard data for battle:', battleId);
          const participantsRef = collection(db, 'public-quiz-participants');
          console.log('Executing leaderboard query for battleId:', battleId);
          const leaderboardQuery = query(
            participantsRef,
            where('battleId', '==', battleId), // Filter by battleId
            where('completed', '==', true),
            orderBy('score', 'desc'),
            // Add secondary order by time taken if available and relevant for ranking
             orderBy('completedAt', 'asc'),
            limit(50) // Limit leaderboard size
          );

          const leaderboardDocs = await getDocs(leaderboardQuery);

          console.log('Fetched raw leaderboard documents:', leaderboardDocs.docs.map(doc => ({ id: doc.id, ...doc.data() })));

           // Process leaderboard to get unique users with best scores (if multiple attempts per user)
           const uniqueUserScores = new Map();
           console.log('Processing leaderboard documents...');
           leaderboardDocs.forEach(doc => {
             const data = doc.data();
             console.log('Processing document:', { id: doc.id, data });
             // Ensure user ID is available and valid for redaction
             if (data.userId) {
                 const existingEntry = uniqueUserScores.get(data.userId);
                 console.log('Existing entry for user', data.userId, ':', existingEntry);

                 if (!existingEntry ||
                     (data.score > existingEntry.score ||
                      (data.score === existingEntry.score &&
                       // Assuming startedAt and completedAt exist and are Timestamps
                       (data.completedAt?.toMillis() - data.startedAt?.toMillis()) <
                       (existingEntry.completedAt?.toMillis() - existingEntry.startedAt?.toMillis())))) {
                   console.log('Adding/Updating entry for user', data.userId, ':', data);
                   uniqueUserScores.set(data.userId, {
                     ...data,
                     // Calculate time taken in seconds
                     timeTaken: (data.completedAt?.toMillis() - data.startedAt?.toMillis()) / 1000,
                     id: doc.id
                   });
                 } else {
                   console.log('Existing entry is better or equal for user', data.userId, ', skipping:', existingEntry);
                 }
              } else {
                 console.warn('Participant document missing userId:', doc.id, data);
              }
           });

          console.log('Finished processing. Unique user scores map:', uniqueUserScores);

          // Convert to array and sort again just in case (should be sorted by query)
          const sortedLeaderboard = Array.from(uniqueUserScores.values())
            .sort((a, b) => {
              if (b.score !== a.score) return b.score - a.score; // Sort by score descending
               // Then sort by time taken ascending
              return (a.timeTaken || Infinity) - (b.timeTaken || Infinity);
            });

          console.log('Sorted leaderboard array:', sortedLeaderboard);

          if (isMounted) {
             // Redact names for other users
             const redactedLeaderboard = sortedLeaderboard.map(entry => ({
                 ...entry,
                  // Redact username unless it's the current user
                 userName: entry.userId === currentUser?.uid ? entry.userName || 'You' : '****',
             }));
            console.log('Redacted leaderboard for display:', redactedLeaderboard);
            setLeaderboard(redactedLeaderboard);
          }
        } catch (leaderboardError) {
          console.error('Error fetching leaderboard:', leaderboardError);
          if (isMounted) {
            setLeaderboardError(leaderboardError.message);
          }
        } finally {
          if (isMounted) {
            setLeaderboardLoading(false);
          }
        }

      } catch (error) {
        console.error('Error fetching battle results:', error);
        if (isMounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      isMounted = false;
    };
  }, [battleId, participantId, score, currentUser?.uid]); // Added currentUser.uid to dependencies

   const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Calculate time taken from participant data
  const calculateTimeTaken = (participant) => {
    if (!participant?.startedAt || !participant?.completedAt) return 0;
    const start = participant.startedAt.toDate();
    const end = participant.completedAt.toDate();
    return Math.round((end - start) / 1000); // Returns time in seconds
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  // Use participant.score if available, otherwise use score from param (less reliable)
  const displayedScore = participant?.score ?? score;

  return (
    <MathJaxContext config={mathJaxConfig}>
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Personal Score Section */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">Quiz Battle Results</h1>
          {battleData?.title && <p className="text-xl text-gray-600 mb-4">{battleData.title}</p>}
          <div className={`text-4xl font-bold ${getScoreColor(displayedScore)}`}>
            {displayedScore}% {/* Use displayedScore */}
          </div>
          <div className="text-gray-600 mt-2">
            {displayedScore >= 75 ? 'Excellent work!' : 
             displayedScore >= 50 ? 'Good effort!' : 
             'Keep practicing!'}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <FiAward className="w-6 h-6 mx-auto mb-2 text-[rgb(130,88,18)]" />
            <div className="text-sm text-gray-600">Your Score</div>
            <div className="font-bold">{displayedScore}%</div> {/* Use displayedScore */}
          </div>
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <FiBarChart2 className="w-6 h-6 mx-auto mb-2 text-[rgb(130,88,18)]" />
            <div className="text-sm text-gray-600">Questions</div>
            <div className="font-bold">{battleData?.questions?.length || 0}</div> {/* Use battleData */}
          </div>
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <FiClock className="w-6 h-6 mx-auto mb-2 text-[rgb(130,88,18)]" />
            <div className="text-sm text-gray-600">Time Taken</div>
            <div className="font-bold">
              {formatTime(calculateTimeTaken(participant))}
            </div>
          </div>
        </div>

        {/* Questions Review Section (optional for battles, adapt if needed) */}
        {/* Display questions, user answers, and correct answers */}
         {battleData?.questions && participant?.answers && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-center">Your Answers</h2>
              <ul className="space-y-6">
                {battleData.questions.map((q, idx) => {
                  const userAnswerIdx = participant.answers[idx];
                  const isCorrect = userAnswerIdx === q.correctOption;
                  return (
                    <li key={idx} className={`p-4 rounded-lg shadow border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}> 
                      <div className="flex justify-between items-start mb-2">
                          <div className="font-semibold">Q{idx + 1}: <MathJax inline={true}>{formatCustomTagsToLaTeX(q.question)}</MathJax></div>
                        {/* Flag button - re-implement if needed for battle results */}
                        {/* <button
                          onClick={() => handleFlagQuestion(q.id)}
                          className={`p-2 rounded-full ${flaggedQuestions.has(q.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                          title="Flag Question"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                        </button> */}
                      </div>
                      <div className="ml-4">
                          <span className="font-medium">Your Answer:</span> {typeof userAnswerIdx === 'number' ? <MathJax inline={true}>{formatCustomTagsToLaTeX(q.options[userAnswerIdx])}</MathJax> : <span className="italic text-gray-400">No answer</span>}
                         {/* Display Correct/Missed status based on isCorrect */}
                         <span className={`ml-4 px-2 py-1 rounded text-xs ${isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>{isCorrect ? 'Correct' : 'Missed'}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

        {/* Leaderboard Section */}
        <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 text-center">Leaderboard</h2>
            {leaderboardLoading ? (
                <div className="flex justify-center items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(130,88,18)]"></div></div>
            ) : leaderboardError ? (
                <p className="text-red-500 text-center">{leaderboardError}</p>
            ) : leaderboard.length === 0 ? (
                 <p className="text-gray-500 text-center">No participants on the leaderboard yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">Rank</th>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">User</th>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">Score (%)</th>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboard.map((entry, index) => (
                                <tr key={entry.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} ${entry.userId === currentUser?.uid ? 'font-bold bg-yellow-100' : ''}`}>
                                    <td className="py-2 px-4 text-sm text-gray-700">{index + 1}</td>
                                    <td className="py-2 px-4 text-sm text-gray-700">{entry.userName || '****'}</td>
                                    <td className="py-2 px-4 text-sm text-gray-700">{entry.score}</td>
                                    <td className="py-2 px-4 text-sm text-gray-700">{formatTime(entry.timeTaken)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center space-x-4">
           {/* Add a button to retry the battle if desired */}
           {/* <button
             onClick={() => navigate(`/take-battle/${battleId}`)} // Link to the take battle page
             className="px-6 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
           >
             Retry Battle
           </button> */}
           <button
             onClick={() => navigate('/dashboard')}
             className="px-6 py-2 bg-gray-200 rounded hover:bg-gray-300"
           >
             Back to Dashboard
           </button>
        </div>
      </div>
    </div>
    </MathJaxContext>
  );
}

export default QuizBattleResults; 